import { useState, useEffect } from 'react'
import Spinner from '@cloudscape-design/components/spinner'
import Box from '@cloudscape-design/components/box'
import SpaceBetween from '@cloudscape-design/components/space-between'
import Tabs from '@cloudscape-design/components/tabs'
import Button from '@cloudscape-design/components/button'
import Alert from '@cloudscape-design/components/alert'
import { useAppStore } from '@/store/app-store'
import { fetchBedrockHealthEvents, type HealthEvent } from '@/engine/health-api'
import {
  colorTextBodyDefault,
  colorTextBodySecondary,
  colorTextAccent,
  colorTextStatusWarning,
  colorTextStatusError,
  colorTextStatusSuccess,
  colorBackgroundContainerHeader,
  colorBorderDividerDefault,
} from '@cloudscape-design/design-tokens'

interface LifecycleModel {
  provider: string
  name: string
  modelId: string
  regions: string
  legacyDate: string
  eolDate: string
  extendedAccessDate: string | null
}

interface LifecycleData {
  lastUpdated: string
  source: string
  models: LifecycleModel[]
}

function daysUntil(dateStr: string): number {
  return Math.ceil((new Date(dateStr).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
}

function statusBadge(model: LifecycleModel): { label: string; color: string; bg: string } {
  const daysToEol = daysUntil(model.eolDate)
  const daysToLegacy = daysUntil(model.legacyDate)
  if (daysToEol <= 0) return { label: 'EOL', color: '#fff', bg: colorTextStatusError }
  if (daysToEol <= 30) return { label: `EOL in ${daysToEol}d`, color: '#fff', bg: colorTextStatusError }
  if (daysToEol <= 90) return { label: `EOL in ${daysToEol}d`, color: '#fff', bg: colorTextStatusWarning }
  if (daysToLegacy <= 0) return { label: 'Legacy', color: '#fff', bg: colorTextStatusWarning }
  return { label: `Legacy in ${daysToLegacy}d`, color: '#fff', bg: colorTextBodySecondary }
}

function eventStatusBadge(event: HealthEvent): { label: string; color: string; bg: string } {
  if (event.statusCode === 'closed') return { label: 'Closed', color: '#fff', bg: colorTextBodySecondary }
  if (event.statusCode === 'upcoming') return { label: 'Upcoming', color: '#fff', bg: colorTextStatusWarning }
  return { label: 'Open', color: '#fff', bg: colorTextStatusError }
}

function AllModelsTab({ data, filter, setFilter }: { data: LifecycleData; filter: string; setFilter: (v: string) => void }) {
  const filtered = data.models.filter(m =>
    !filter || m.provider.toLowerCase().includes(filter.toLowerCase()) ||
    m.name.toLowerCase().includes(filter.toLowerCase()) ||
    m.modelId.toLowerCase().includes(filter.toLowerCase())
  ).sort((a, b) => new Date(a.eolDate).getTime() - new Date(b.eolDate).getTime())

  return (
    <>
      <p style={{ color: colorTextBodySecondary, fontSize: 13, margin: '0 0 16px' }}>
        Models in Legacy or approaching EOL on Amazon Bedrock. Data from{' '}
        <a href={data.source} target="_blank" rel="noopener noreferrer" style={{ color: colorTextAccent }}>AWS docs</a>.
        Last updated: {data.lastUpdated}
      </p>
      <input
        style={styles.search}
        placeholder="Filter by provider, model name, or model ID..."
        value={filter}
        onChange={e => setFilter(e.target.value)}
        aria-label="Filter models"
      />
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Provider</th>
              <th style={styles.th}>Model</th>
              <th style={styles.th}>Model ID</th>
              <th style={styles.th}>Legacy Date</th>
              <th style={styles.th}>EOL Date</th>
              <th style={styles.th}>Extended Access</th>
              <th style={styles.th}>Regions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((m, i) => {
              const badge = statusBadge(m)
              return (
                <tr key={`${m.modelId}-${i}`} style={{ backgroundColor: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)' }}>
                  <td style={styles.td}>
                    <span style={{ ...styles.badge, backgroundColor: badge.bg, color: badge.color }}>{badge.label}</span>
                  </td>
                  <td style={styles.td}>{m.provider}</td>
                  <td style={{ ...styles.td, fontWeight: 500, color: colorTextBodyDefault }}>{m.name}</td>
                  <td style={{ ...styles.td, fontFamily: 'monospace', fontSize: 11 }}>{m.modelId}</td>
                  <td style={styles.td}>{m.legacyDate}</td>
                  <td style={{ ...styles.td, color: daysUntil(m.eolDate) <= 90 ? colorTextStatusWarning : colorTextBodySecondary, fontWeight: daysUntil(m.eolDate) <= 90 ? 600 : 400 }}>
                    {m.eolDate}
                  </td>
                  <td style={styles.td}>{m.extendedAccessDate || '-'}</td>
                  <td style={{ ...styles.td, fontSize: 11, color: colorTextBodySecondary, maxWidth: 200 }}>{m.regions}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </>
  )
}

function YourAccountTab() {
  const executionMode = useAppStore((s) => s.executionMode)
  const credentialStatus = useAppStore((s) => s.credentialStatus)
  const [events, setEvents] = useState<HealthEvent[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [permissionDenied, setPermissionDenied] = useState(false)
  const [expandedArn, setExpandedArn] = useState<string | null>(null)

  if (executionMode !== 'local') {
    return (
      <Alert type="info">
        Account-specific model lifecycle data is only available in Local mode. Switch to Local mode in Settings and configure your AWS credentials.
      </Alert>
    )
  }

  if (credentialStatus !== 'verified') {
    return (
      <Alert type="warning">
        AWS credentials required. Go to Settings to configure your credentials, then return here to check your account's model deprecation events.
      </Alert>
    )
  }

  const handleFetch = async () => {
    setLoading(true)
    setError(null)
    setPermissionDenied(false)
    try {
      const result = await fetchBedrockHealthEvents()
      setEvents(result.events)
      if (result.error) {
        setError(result.error)
        setPermissionDenied(result.permissionDenied || false)
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to fetch health events')
    } finally {
      setLoading(false)
    }
  }

  return (
    <SpaceBetween size="m">
      <p style={{ color: colorTextBodySecondary, fontSize: 13, margin: 0 }}>
        Fetches Bedrock model deprecation and lifecycle events from your AWS account's Personal Health Dashboard (PHD).
        Requires <code style={{ fontSize: 11 }}>health:DescribeEvents</code>, <code style={{ fontSize: 11 }}>health:DescribeEventDetails</code>, and <code style={{ fontSize: 11 }}>health:DescribeAffectedEntities</code> permissions, plus a Business or Enterprise Support plan.
      </p>

      <Button onClick={handleFetch} loading={loading} iconName="refresh">
        {events.length > 0 ? 'Refresh' : 'Fetch Health Events'}
      </Button>

      {permissionDenied && (
        <Alert type="error">{error}</Alert>
      )}

      {error && !permissionDenied && (
        <Alert type="warning">{error}</Alert>
      )}

      {!loading && !error && events.length === 0 && !permissionDenied && (
        <Box color="text-body-secondary" fontSize="body-s">
          {loading ? '' : 'Click "Fetch Health Events" to check your account for Bedrock model deprecation notices.'}
        </Box>
      )}

      {events.length > 0 && (
        <>
          <Alert type="success">
            Found {events.length} Bedrock health event{events.length !== 1 ? 's' : ''} for your account.
          </Alert>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Event Type</th>
                  <th style={styles.th}>Region</th>
                  <th style={styles.th}>Start Date</th>
                  <th style={styles.th}>End Date</th>
                  <th style={styles.th}>Affected Models</th>
                  <th style={styles.th}>Details</th>
                </tr>
              </thead>
              <tbody>
                {events.map((e, i) => {
                  const badge = eventStatusBadge(e)
                  const isExpanded = expandedArn === e.eventArn
                  return (
                    <tr key={e.eventArn || i} style={{ backgroundColor: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)', verticalAlign: 'top' }}>
                      <td style={styles.td}>
                        <span style={{ ...styles.badge, backgroundColor: badge.bg, color: badge.color }}>{badge.label}</span>
                      </td>
                      <td style={{ ...styles.td, fontFamily: 'monospace', fontSize: 11 }}>
                        {e.eventTypeCode.replace('AWS_BEDROCK_', '')}
                      </td>
                      <td style={styles.td}>{e.region}</td>
                      <td style={styles.td}>{e.startTime ? new Date(e.startTime).toLocaleDateString() : '-'}</td>
                      <td style={{ ...styles.td, color: e.endTime ? colorTextStatusWarning : colorTextBodySecondary }}>
                        {e.endTime ? new Date(e.endTime).toLocaleDateString() : 'Ongoing'}
                      </td>
                      <td style={{ ...styles.td, fontSize: 11 }}>
                        {e.affectedEntities.length > 0
                          ? e.affectedEntities.map(ae => ae.entityValue).join(', ')
                          : '-'}
                      </td>
                      <td style={styles.td}>
                        {e.description && (
                          <button
                            onClick={() => setExpandedArn(isExpanded ? null : e.eventArn)}
                            style={{ background: 'none', border: 'none', color: colorTextAccent, cursor: 'pointer', fontSize: 11, padding: 0 }}
                          >
                            {isExpanded ? 'Hide' : 'Show'}
                          </button>
                        )}
                        {isExpanded && e.description && (
                          <div style={{ marginTop: 8, fontSize: 11, color: colorTextBodySecondary, whiteSpace: 'pre-wrap', maxWidth: 400 }}>
                            {e.description}
                          </div>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </SpaceBetween>
  )
}

export function ModelLifecyclePage() {
  const [data, setData] = useState<LifecycleData | null>(null)
  const [filter, setFilter] = useState('')

  useEffect(() => {
    const loadData = async () => {
      try {
        const res = await fetch('/data/model-lifecycle.json')
        if (res.ok) {
          const d = await res.json()
          if (d && d.models) setData(d)
        }
      } catch { /* ignore */ }
    }
    loadData()
  }, [])

  if (!data) return (
    <Box textAlign="center" padding={{ vertical: 'xxl' }}>
      <Spinner size="large" />
    </Box>
  )

  return (
    <div style={{ padding: 24 }}>
      <h2 style={{ color: colorTextBodyDefault, margin: '0 0 16px', fontSize: 20 }}>Model Lifecycle</h2>
      <Tabs
        tabs={[
          {
            label: 'All Models',
            id: 'all-models',
            content: <AllModelsTab data={data} filter={filter} setFilter={setFilter} />,
          },
          {
            label: 'Your Account (PHD)',
            id: 'your-account',
            content: <YourAccountTab />,
          },
        ]}
      />
    </div>
  )
}

// Exported helper for use in ProfileEditor warnings
export function getEolWarning(modelId: string, lifecycleModels: LifecycleModel[]): string | null {
  if (!modelId || !lifecycleModels.length) return null
  const baseId = modelId.replace(/^(us|eu|ap|apac|global|jp|au|me|sa|af|ca)\./, '')
  const match = lifecycleModels.find(m => m.modelId === baseId || m.modelId === modelId)
  if (!match) return null
  const daysToEol = daysUntil(match.eolDate)
  if (daysToEol <= 0) return `⛔ ${match.name} has reached EOL (${match.eolDate}). It is no longer available.`
  if (daysToEol <= 90) return `⚠️ ${match.name} reaches EOL in ${daysToEol} days (${match.eolDate}). Plan migration to an active model.`
  return null
}

export type { LifecycleModel }

const styles: Record<string, React.CSSProperties> = {
  search: { width: '100%', padding: '8px 12px', backgroundColor: colorBackgroundContainerHeader, border: `1px solid ${colorBorderDividerDefault}`, borderRadius: 6, color: colorTextBodyDefault, fontSize: 13, marginBottom: 16, boxSizing: 'border-box' },
  th: { textAlign: 'left', padding: '8px 10px', color: colorTextBodySecondary, borderBottom: `1px solid ${colorBorderDividerDefault}`, fontWeight: 500, whiteSpace: 'nowrap' },
  td: { padding: '8px 10px', color: colorTextBodySecondary, borderBottom: `1px solid ${colorBorderDividerDefault}` },
  badge: { display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' as const },
}
